import { AsyncPipe, NgIf } from '@angular/common';
import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { BoondockFacade } from '@curbnturf/boondock-listing';
import { Boondock, IBoondock } from '@curbnturf/entities';
import { IConditionsRatingForm, INoiseRatingForm, IRvRoadConditionsForm } from '@curbnturf/form-helpers';
import { RatingFacade } from '@curbnturf/rating';
import * as RatingActions from '@curbnturf/rating/src/lib/+state/rating.actions';
import { IonicModule, LoadingController, NavController } from '@ionic/angular';
import { Actions, ofType } from '@ngrx/effects';
import { Observable, Subscription } from 'rxjs';
import { RatingIncrementalComponent } from '../rating-incremental/rating-incremental.component';
import { RatingToggleButtonComponent } from '../rating-toggle-button/rating-toggle-button.component';

@Component({
  selector: 'curbnturf-rating-boondock-conditions',
  templateUrl: './rating-boondock-conditions.component.html',
  styleUrls: ['./rating-boondock-conditions.component.scss'],
  standalone: true,
  imports: [
    AsyncPipe,
    FormsModule,
    IonicModule,
    NgIf,
    RatingIncrementalComponent,
    RatingToggleButtonComponent,
    ReactiveFormsModule,
  ],
})
export class RatingBoondockConditionsComponent implements OnInit {
  @Output() navigateBack: EventEmitter<void> = new EventEmitter<void>();

  @Output() continue: EventEmitter<void> = new EventEmitter<void>();

  @Output() skip: EventEmitter<void> = new EventEmitter<void>();

  subscriptions: Subscription[] = [];

  boondock$: Observable<Boondock | null>;
  boondockId: number | undefined;

  appContext: boolean;
  caretakerBoondock: number[];

  conditionsForm: FormGroup<IConditionsRatingForm>;

  get isCaretaker() {
    return !!(this.boondockId && this.caretakerBoondock.includes(this.boondockId));
  }

  constructor(
    private updates$: Actions,
    private route: ActivatedRoute,
    private fb: FormBuilder,
    private loadingController: LoadingController,
    private navCtrl: NavController,
    private ratingFacade: RatingFacade,
    private boondockFacade: BoondockFacade,
  ) {}

  ngOnInit(): void {
    this.conditionsForm = this.fb.group<IConditionsRatingForm>({
      noiseLevel: this.fb.group<INoiseRatingForm>({
        silence: this.fb.control(false),
        nature: this.fb.control(false),
        periodic: this.fb.control(false),
        constant: this.fb.control(false),
      }),
      road: this.fb.group<IRvRoadConditionsForm>({
        paved: this.fb.control(false),
        smoothGravel: this.fb.control(false),
        poorGravel: this.fb.control(false),
        highClearance: this.fb.control(false),
        offRoad: this.fb.control(false),
      }),
      buses: this.fb.control(0),
      cars: this.fb.control(0),
      hazards: this.fb.control(false),
      hazardDescription: this.fb.control(''),
      wheelchair: this.fb.control(false),
    });

    this.boondock$ = this.boondockFacade.selected$;
    this.subscriptions.push(
      this.ratingFacade.caretakerSites$.subscribe((boondocks) => {
        this.caretakerBoondock = boondocks;
      }),
      this.boondock$.subscribe((boondock) => {
        if (boondock) {
          this.boondockId = boondock.id;
          this.populateFields(boondock);
        }
      }),
      this.route.data.subscribe((data: { appContext: boolean }) => {
        this.appContext = data.appContext || false;
      }),
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub) => {
      if (!sub.closed) {
        sub.unsubscribe();
      }
    });
  }

  populateFields(boondock: Boondock) {
    this.conditionsForm.patchValue({
      noiseLevel: {
        silence: boondock.noise?.level === 'silence',
        nature: boondock.noise?.level === 'nature',
        periodic: boondock.noise?.level === 'periodic',
        constant: boondock.noise?.level === 'constant',
      },
      road: {
        paved: boondock.roadCondition.paved,
        smoothGravel: boondock.roadCondition.smoothGravel,
        poorGravel: boondock.roadCondition.poorGravel,
        highClearance: boondock.roadCondition.highClearance,
        offRoad: boondock.roadCondition.offRoad,
      },
      buses: boondock.sizeBuses,
      cars: boondock.sizeCars,
      hazards: boondock.potentialHazards,
      hazardDescription: boondock.potentialHazardDescription,
      wheelchair: !!boondock.amenities?.find((el) => el.type === 'wheelchair'),
    });
  }

  async onContinue() {
    const boondock: IBoondock = {
      id: this.boondockId,
    };

    if (this.conditionsForm.value.buses) {
      boondock.sizeBuses = this.conditionsForm.value.buses;
    }

    if (this.conditionsForm.value.cars) {
      boondock.sizeCars = this.conditionsForm.value.cars;
    }

    if (this.conditionsForm.value.hazards) {
      boondock.potentialHazards = this.conditionsForm.value.hazards;
    }

    if (this.conditionsForm.value.hazardDescription) {
      boondock.potentialHazardDescription = this.conditionsForm.value.hazardDescription;
    }

    if (this.conditionsForm.value.wheelchair) {
      boondock.amenities = [
        {
          type: 'wheelchair',
          boondock: { id: this.boondockId },
        },
      ];
    }

    if (this.conditionsForm.value?.noiseLevel?.silence) {
      boondock.noise = { level: 'silence' };
    } else if (this.conditionsForm.value?.noiseLevel?.nature) {
      boondock.noise = { level: 'nature' };
    } else if (this.conditionsForm.value?.noiseLevel?.periodic) {
      boondock.noise = { level: 'periodic' };
    } else if (this.conditionsForm.value?.noiseLevel?.constant) {
      boondock.noise = { level: 'constant' };
    }

    const roadCondition = {
      paved: false,
      pavedDescription: '',
      smoothGravel: false,
      smoothGravelDescription: '',
      poorGravel: false,
      poorGravelDescription: '',
      highClearance: false,
      highClearanceDescription: '',
      offRoad: false,
      offRoadDescription: '',
    };
    let setRoadCondition = false;
    if (this.conditionsForm.value?.road?.paved) {
      roadCondition.paved = true;
      setRoadCondition = true;
    } else if (this.conditionsForm.value?.road?.smoothGravel) {
      roadCondition.smoothGravel = true;
      setRoadCondition = true;
    } else if (this.conditionsForm.value?.road?.poorGravel) {
      roadCondition.poorGravel = true;
      setRoadCondition = true;
    } else if (this.conditionsForm.value?.road?.highClearance) {
      roadCondition.highClearance = true;
      setRoadCondition = true;
    } else if (this.conditionsForm.value?.road?.offRoad) {
      roadCondition.offRoad = true;
      setRoadCondition = true;
    }

    if (setRoadCondition) {
      boondock.accessRoutes = [
        {
          roadCondition,
        },
      ];
    }

    const loading = await this.loadingController.create({
      message: 'Saving...',
      duration: 2000,
    });
    await loading.present();

    // Make the subscription before sending the request to make sure we don't miss anything.
    this.subscriptions.push(
      this.updates$.pipe(ofType(RatingActions.selectCaretakerBoondock)).subscribe(async () => {
        await loading.dismiss();

        // Emit event for web and navigate for app.
        if (this.appContext) {
          await this.navCtrl.navigateForward(['rating', 'boondock', this.boondockId, 'description']);
        } else {
          this.continue.emit();
        }
      }),
    );

    this.ratingFacade.selectCaretakerBoondock({
      boondockId: this.boondockId,
      boondock,
    });
  }

  async onSkip() {
    // Emit event for web and navigate for app.
    if (this.appContext) {
      await this.navCtrl.navigateForward(['rating', 'boondock', this.boondockId, 'description']);
    } else {
      this.skip.emit();
    }
  }

  onSelectRoadCondition(paved: string) {
    switch (paved) {
      case 'paved':
        this.conditionsForm.patchValue({
          road: {
            smoothGravel: false,
            poorGravel: false,
            highClearance: false,
            offRoad: false,
          },
        });
        break;
      case 'smoothGravel':
        this.conditionsForm.patchValue({
          road: {
            paved: false,
            poorGravel: false,
            highClearance: false,
            offRoad: false,
          },
        });
        break;
      case 'poorGravel':
        this.conditionsForm.patchValue({
          road: {
            paved: false,
            smoothGravel: false,
            highClearance: false,
            offRoad: false,
          },
        });
        break;
      case 'highClearance':
        this.conditionsForm.patchValue({
          road: {
            paved: false,
            smoothGravel: false,
            poorGravel: false,
            offRoad: false,
          },
        });
        break;
      case 'offRoad':
        this.conditionsForm.patchValue({
          road: {
            paved: false,
            smoothGravel: false,
            poorGravel: false,
            highClearance: false,
          },
        });
        break;
      default:
        this.conditionsForm.patchValue({
          road: {
            paved: false,
            smoothGravel: false,
            poorGravel: false,
            highClearance: false,
            offRoad: false,
          },
        });
        break;
    }
  }

  onSelectNoiseLevel(noise: string) {
    switch (noise) {
      case 'silence':
        this.conditionsForm.patchValue({
          noiseLevel: {
            nature: false,
            periodic: false,
            constant: false,
          },
        });
        break;
      case 'nature':
        this.conditionsForm.patchValue({
          noiseLevel: {
            silence: false,
            periodic: false,
            constant: false,
          },
        });
        break;
      case 'periodic':
        this.conditionsForm.patchValue({
          noiseLevel: {
            silence: false,
            nature: false,
            constant: false,
          },
        });
        break;
      case 'constant':
        this.conditionsForm.patchValue({
          noiseLevel: {
            silence: false,
            nature: false,
            periodic: false,
          },
        });
        break;
      default:
        this.conditionsForm.patchValue({
          noiseLevel: {
            silence: false,
            nature: false,
            periodic: false,
            constant: false,
          },
        });
        break;
    }
  }
}
